import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Heart, 
  LayoutDashboard, 
  Users, 
  BarChart3, 
  Settings, 
  LogOut,
  Menu,
  X,
  Brain,
  Mic,
  TrendingUp,
  CreditCard
} from 'lucide-react'
import { useState, useEffect } from 'react'

import { useAuth } from '../hooks/useAuth'
import { Button } from './ui/Button'
import { getUserSubscription } from '../lib/stripe'

interface NavigationProps {
  variant?: 'landing' | 'dashboard'
}

export const Navigation: React.FC<NavigationProps> = ({ variant = 'landing' }) => {
  const { user, appUser, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)

  useEffect(() => {
    if (user) {
      loadSubscription()
    }
  }, [user])

  const loadSubscription = async () => {
    try {
      const { subscription: subData } = await getUserSubscription()
      setSubscription(subData)
    } catch (error) {
      console.error('Error loading subscription:', error)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const isProfessionalSubscriber = subscription?.product?.name?.toLowerCase().includes('professional') &&
                                  (subscription.subscription_status === 'active' || 
                                   subscription.subscription_status === 'trialing')

  if (variant === 'dashboard' && user) {
    const navItems = [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Team', href: '/employees', icon: Users },
      { name: 'Insights', href: '/reports', icon: BarChart3 },
      { name: 'Analytics', href: '/analytics', icon: TrendingUp, premium: true },
      { name: 'Assessments', href: '/assessments', icon: Mic },
      { name: 'Settings', href: '/settings', icon: Settings },
    ]

    return (
      <nav className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-3">
              <motion.div 
                className="w-10 h-10 bg-sage-600 rounded-xl flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Brain className="w-6 h-6 text-white" />
              </motion.div>
              <span className="text-xl font-bold text-slate-900">Mentra</span>
              {subscription?.subscription_status === 'active' && (
                <span className="ml-2 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                  {subscription.product?.name || 'Pro'}
                </span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href
                const isPremiumFeature = item.premium && !isProfessionalSubscriber
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-sage-100 text-sage-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    } ${isPremiumFeature ? 'opacity-50' : ''}`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="font-medium">{item.name}</span>
                    {isPremiumFeature && (
                      <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                        Pro
                      </span>
                    )}
                  </Link>
                )
              })}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:block text-sm text-slate-600 font-medium">
                {appUser?.email}
              </div>
              <Button
                variant="ghost"
                onClick={handleSignOut}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 text-slate-600"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Sign Out</span>
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-600"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-slate-200 py-4"
            >
              <div className="space-y-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.href
                  const isPremiumFeature = item.premium && !isProfessionalSubscriber
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-sage-100 text-sage-700'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      } ${isPremiumFeature ? 'opacity-50' : ''}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.name}</span>
                      {isPremiumFeature && (
                        <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Pro
                        </span>
                      )}
                    </Link>
                  )
                })}
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="px-3 py-2 text-sm text-slate-600">
                    {appUser?.email}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={handleSignOut}
                    className="w-full justify-start text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </nav>
    )
  }

  // Landing page navigation
  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md border-b border-slate-200 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3">
            <motion.div 
              className="w-10 h-10 bg-sage-600 rounded-xl flex items-center justify-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Brain className="w-6 h-6 text-white" />
            </motion.div>
            <span className="text-xl font-bold text-slate-900">Mentra</span>
          </Link>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Features</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">How it Works</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Pricing</a>
            
            <div className="flex items-center space-x-4">
              <Link 
                to="/auth/login" 
                className="text-slate-600 hover:text-slate-900 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-slate-100"
              >
                Sign In
              </Link>
              <Link to="/auth/register">
                <Button className="bg-sage-600 hover:bg-sage-700 text-white px-6 py-2">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-600"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-200 py-4"
          >
            <div className="space-y-2">
              <a 
                href="#features" 
                className="block px-3 py-2 text-slate-600 hover:text-slate-900 font-medium rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
              <a 
                href="#how-it-works" 
                className="block px-3 py-2 text-slate-600 hover:text-slate-900 font-medium rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                How it Works
              </a>
              <a 
                href="#pricing" 
                className="block px-3 py-2 text-slate-600 hover:text-slate-900 font-medium rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
              
              <div className="space-y-2 px-3 pt-4 border-t border-slate-200">
                <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-slate-300 text-slate-700 hover:bg-slate-50">
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-sage-600 hover:bg-sage-700 text-white">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  )
}